import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  HiOutlinePresentationChartLine,
  HiOutlineArrowLeft,
  HiOutlineCalendar,
  HiOutlineArrowDownTray,
  HiOutlineAdjustmentsHorizontal,
  HiOutlineChevronDown,
  HiOutlineChevronUp,
} from "react-icons/hi2";
import {
  ComposedChart,
  BarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import ReportFilterBar from "../../components/ReportFilterBar";
import masterDataService from "../../services/masterDataService";
import authService from "../../services/authService";
import "../Reports.css";
import apiClient from "../../config/api";
import {
  showReportGeneratingToast,
  reportGenerationDone,
  reportGenerationFailed,
} from "../../utils/reportGenerationToast";

// ─── Date utilities ──────────────────────────────────────────────────────────
const buildDayRange = (startStr, endStr) => {
  if (!startStr || !endStr) return [];
  const dates = [];
  const cur = new Date(startStr);
  const end = new Date(endStr);
  while (cur <= end) {
    dates.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
};

const buildMonthRange = (startStr, endStr) => {
  if (!startStr || !endStr) return [];
  const dates = [];
  const cur = new Date(startStr);
  cur.setDate(1); // Start at beginning of month
  const end = new Date(endStr);

  while (cur <= end) {
    dates.push(new Date(cur));
    cur.setMonth(cur.getMonth() + 1);
  }
  return dates;
};

const buildQuarterRange = (startStr, endStr) => {
  if (!startStr || !endStr) return [];
  const dates = [];
  const cur = new Date(startStr);
  cur.setDate(1);
  cur.setMonth(Math.floor(cur.getMonth() / 3) * 3);
  const end = new Date(endStr);

  while (cur <= end) {
    dates.push(new Date(cur));
    cur.setMonth(cur.getMonth() + 3);
  }
  return dates;
};

const buildYearRange = (startStr, endStr) => {
  if (!startStr || !endStr) return [];
  const dates = [];
  const cur = new Date(startStr);
  cur.setDate(1);
  cur.setMonth(0);
  const end = new Date(endStr);

  while (cur <= end) {
    dates.push(new Date(cur));
    cur.setFullYear(cur.getFullYear() + 1);
  }
  return dates;
};

const periodLabel = (d, granularity) => {
  if (granularity === 'daily') {
    return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: '2-digit' });
  } else if (granularity === 'quarterly') {
    return `Q${Math.floor(d.getMonth() / 3) + 1} '${d.getFullYear().toString().slice(-2)}`;
  } else if (granularity === 'yearly') {
    return d.getFullYear().toString();
  }
  return d.toLocaleDateString(undefined, { month: "short", year: "2-digit" });
};

// ─── Data Processor ─────────────────────────────────────────────────────
// Removes computeLossesFromRecord, replacing heuristic logic.

const processTrendsData = (rawRecords, periods, activeLossTypes, granularity) => {
  const periodBuckets = periods.map(date => periodLabel(date, granularity));
  const aggregated = {};

  activeLossTypes.forEach(lt => {
    aggregated[lt.name] = {};
    lt.subTypes.forEach(st => {
      aggregated[lt.name][st.name] = {};
      periodBuckets.forEach(p => {
        aggregated[lt.name][st.name][p] = { loss: 0, freq: 0 };
      });
    });
  });

  rawRecords.forEach(rec => {
    const recDate = new Date(rec.date);
    const pLabel = periodLabel(recDate, granularity);
    if (!periodBuckets.includes(pLabel)) return;

    activeLossTypes.forEach(lt => {
      // Compare trimmed & lowercased for robustness
      const recLT = (rec.lossTypeName || "").trim().toLowerCase();
      const filterLT = (lt.name || "").trim().toLowerCase();
      if (recLT !== filterLT) return;

      lt.subTypes.forEach(st => {
        const recST = (rec.lossSubTypeName || "").trim().toLowerCase();
        const filterST = (st.name || "").trim().toLowerCase();

        if (st.name === 'All SubTypes' || recST === filterST) {
          if (aggregated[lt.name][st.name] && aggregated[lt.name][st.name][pLabel]) {
            aggregated[lt.name][st.name][pLabel].freq += rec.durationMinutes;
            aggregated[lt.name][st.name][pLabel].loss += rec.occurrences;
          }
        }
      });
    });
  });

  return activeLossTypes.map(lt => {
    let chartAggregatedData = periodBuckets.map(p => ({ period: p, loss: 0, freq: 0 }));

    const enrichedSubTypes = lt.subTypes.map(st => {
      const dataPoints = periodBuckets.map(p => {
        const bucket = aggregated[lt.name][st.name][p] || { loss: 0, freq: 0 };
        const rFreq = Math.round(bucket.freq * 10) / 10;
        return { period: p, loss: bucket.loss, freq: rFreq };
      });

      dataPoints.forEach((dp, i) => {
        chartAggregatedData[i].loss += dp.loss;
        chartAggregatedData[i].freq += dp.freq;
      });

      return { ...st, data: dataPoints };
    });

    chartAggregatedData.forEach(dp => {
      dp.freq = Math.round(dp.freq * 10) / 10;
      dp.loss = Math.round(dp.loss);
    });

    return {
      lossTypeId: lt.id,
      lossTypeName: lt.name,
      subTypes: enrichedSubTypes,
      data: chartAggregatedData,
    };
  });
};


// ─── Component ───────────────────────────────────────────────────────────────
const OeeLossTrendsReport = () => {
  const navigate = useNavigate();

  const [showFilters, setShowFilters] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [rawData, setRawData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [allAvailableLossTypes, setAllAvailableLossTypes] = useState([]);
  const [allAvailableLossSubTypes, setAllAvailableLossSubTypes] = useState([]);

  // Filters
  const [selectedPlants, setSelectedPlants] = useState([]);
  const [selectedLines, setSelectedLines] = useState([]);
  const [selectedMachines, setSelectedMachines] = useState([]);
  const [selectedShifts, setSelectedShifts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedSkus, setSelectedSkus] = useState([]);
  const [selectedLossTypes, setSelectedLossTypes] = useState([]);
  const [selectedLossSubTypes, setSelectedLossSubTypes] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Modal State
  const [selectedChartData, setSelectedChartData] = useState(null);

  useEffect(() => {
    const fetchLossData = async () => {
      try {
        const tenantId = authService.getTenantId();
        const types = await masterDataService.getLossTypes(tenantId);
        setAllAvailableLossTypes(types || []);

        const subTypes = await masterDataService.getLossSubTypes(tenantId);
        setAllAvailableLossSubTypes(subTypes || []);
      } catch (err) {
        console.error("Failed to fetch loss types for defaults", err);
      }
    };
    fetchLossData();
  }, []);

  const [viewType, setViewType] = useState("table");
  const [granularity, setGranularity] = useState("monthly");
  const [selectedMetrics, setSelectedMetrics] = useState({
    occurrence: true,
    timeLoss: true,
    lossType: true,
    lossSubType: false
  });


  const getActiveLossTypesWithSubTypes = () => {
    return allAvailableLossTypes
      .filter(lt => selectedLossTypes.includes(lt.lossTypeId))
      .map(lt => {
        let relevantSubTypes = [];
        if (selectedLossSubTypes && selectedLossSubTypes.length > 0) {
          relevantSubTypes = allAvailableLossSubTypes
            .filter(st => st.lossTypeId === lt.lossTypeId && selectedLossSubTypes.includes(st.subTypeId))
            .map(st => ({ id: st.subTypeId, name: st.subTypeName }));
        }
        if (relevantSubTypes.length === 0) {
          relevantSubTypes = [{ id: 'all', name: 'All SubTypes' }];
        }
        return { id: lt.lossTypeId, name: lt.lossTypeName, subTypes: relevantSubTypes };
      });
  };

  const activeFilterCount =
    selectedPlants.length +
    selectedLines.length +
    selectedMachines.length +
    selectedShifts.length +
    selectedProducts.length +
    selectedSkus.length +
    selectedLossTypes.length +
    selectedLossSubTypes.length +
    (startDate ? 1 : 0) +
    (endDate ? 1 : 0);

  const handleGenerate = async () => {
    if (!startDate || !endDate) {
      setError("Please select both Start Date and End Date.");
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      setError("Start Date must be before End Date.");
      return;
    }
    if (selectedLossTypes.length === 0) {
      setError("Please select at least one Loss Type.");
      return;
    }
    setError("");
    showReportGeneratingToast();
    setLoading(true);

    try {
      const token = localStorage.getItem("authToken") || sessionStorage.getItem("authToken");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = {
        startDate,
        endDate,
        ...(selectedPlants.length > 0 && { plantId: selectedPlants[0] }),
        ...(selectedLines.length > 0 && { lineId: selectedLines[0] }),
        ...(selectedMachines.length > 0 && { machineId: selectedMachines[0] }),
        ...(selectedShifts.length > 0 && { shift: selectedShifts[0] }),
        ...(selectedProducts.length > 0 && { productId: selectedProducts[0] }),
        ...(selectedSkus.length > 0 && { skuId: selectedSkus[0] }),
      };

      const res = await apiClient.get("/reports/oee-loss-trends", { params, headers });
      const fetchedRawData = res.data;
      setRawData(fetchedRawData);

      const activeLossTypes = getActiveLossTypesWithSubTypes();

      let periods = [];
      if (granularity === 'daily') periods = buildDayRange(startDate, endDate);
      else if (granularity === 'quarterly') periods = buildQuarterRange(startDate, endDate);
      else if (granularity === 'yearly') periods = buildYearRange(startDate, endDate);
      else periods = buildMonthRange(startDate, endDate);

      // If only dates selected within the same month/period, just show that period
      if (periods.length === 0) {
        periods.push(new Date(startDate));
      }

      const generatedData = processTrendsData(fetchedRawData, periods, activeLossTypes, granularity);

      setReportData({
        periods,
        series: generatedData,
      });
      // setShowFilters(false);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch report data. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGranularityChange = (e) => {
    const newGran = e.target.value;
    setGranularity(newGran);

    if (reportData && rawData && startDate && endDate) {
      const activeLossTypes = getActiveLossTypesWithSubTypes();

      let periods = [];
      if (newGran === 'daily') periods = buildDayRange(startDate, endDate);
      else if (newGran === 'quarterly') periods = buildQuarterRange(startDate, endDate);
      else if (newGran === 'yearly') periods = buildYearRange(startDate, endDate);
      else periods = buildMonthRange(startDate, endDate);

      if (periods.length === 0) {
        periods.push(new Date(startDate));
      }

      const generatedData = processTrendsData(rawData, periods, activeLossTypes, newGran);

      setReportData({
        periods,
        series: generatedData,
      });
    }
  };

  const clearAllFilters = () => {
    setSelectedPlants([]);
    setSelectedLines([]);
    setSelectedMachines([]);
    setSelectedShifts([]);
    setSelectedProducts([]);
    setSelectedSkus([]);
    setSelectedLossTypes([]);
    setSelectedLossSubTypes([]);
    setStartDate("");
    setEndDate("");
    setReportData(null);
    setRawData(null);
    setError("");
  };

  const handleChartClick = (seriesGroup) => {
    setSelectedChartData({
      isSubLossBreakdown: false,
      lossTypeName: seriesGroup.lossTypeName,
      data: seriesGroup.data
    });
  };

  const handleBarClick = (entry, index, seriesGroup, e) => {
    if (e) e.stopPropagation();
    const dataPoint = entry.payload || entry;
    if (!seriesGroup || !seriesGroup.subTypes || !dataPoint.period) return;

    const subTypesBreakdown = seriesGroup.subTypes.map(st => {
      const match = st.data.find(d => d.period === dataPoint.period);
      return {
        name: st.name,
        loss: match ? match.loss : 0,
        freq: match ? match.freq : 0,
      };
    });

    setSelectedChartData({
      isSubLossBreakdown: true,
      period: dataPoint.period,
      lossTypeName: seriesGroup.lossTypeName,
      data: subTypesBreakdown
    });
  };

  const closeChartModal = () => {
    setSelectedChartData(null);
  };


  return (
    <div className="pagecontainer report-page">
      <div className="page-header">
        <div className="header-left d-flex align-items-center">
          <button
            className="oee-report-back-btn mr-2"
            onClick={() => navigate("/home/reports")}
            title="Back to OEE Reports"
          >
            <HiOutlineArrowLeft size={18} />
          </button>
          <div>
            <h1 className="oee-report-title">
              <HiOutlinePresentationChartLine />
              Loss Trends Report
            </h1>
            <p className="oee-report-subtitle">
              Analyze occurrences and frequency trends over time
            </p>
          </div>
        </div>
        <div className="header-actions">
          <button
            className="oee-filter-toggle-btn"
            onClick={() => setShowFilters((prev) => !prev)}
            title={showFilters ? "Hide Filters" : "Show Filters"}
          >
            <HiOutlineAdjustmentsHorizontal size={16} />
            Filters
            {activeFilterCount > 0 && (
              <span className="oee-filter-badge">{activeFilterCount}</span>
            )}
            {showFilters ? (
              <HiOutlineChevronUp size={13} />
            ) : (
              <HiOutlineChevronDown size={13} />
            )}
          </button>


          <button
            className="btn-primary btn"
            disabled={!reportData}
            title={reportData ? "Export to Excel" : "Generate a report first"}
          >
            <HiOutlineArrowDownTray />
            Export
          </button>
        </div>
      </div>
      {showFilters && (
        <div className="reportbox searchfilterbox">
          <div className="report-filters">
            <div className="filter-group">
              <label>Start Date</label>
              <input
                type="date"
                className={`filter-input${error ? " input-error" : ""}`}
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setError("");
                }}
              />
            </div>
            <div className="filter-group">
              <label>End Date</label>
              <input
                type="date"
                className={`filter-input${error ? " input-error" : ""}`}
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setError("");
                }}
              />
            </div>
            <ReportFilterBar
              selectedPlants={selectedPlants}
              onPlantChange={setSelectedPlants}
              selectedLines={selectedLines}
              onLineChange={setSelectedLines}
              selectedMachines={selectedMachines}
              onMachineChange={setSelectedMachines}
              selectedShifts={selectedShifts}
              onShiftChange={setSelectedShifts}
              selectedProducts={selectedProducts}
              onProductChange={setSelectedProducts}
              selectedSkus={selectedSkus}
              onSkuChange={setSelectedSkus}
              selectedLossTypes={selectedLossTypes}
              onLossTypeChange={setSelectedLossTypes}
              selectedLossSubTypes={selectedLossSubTypes}
              onLossSubTypeChange={setSelectedLossSubTypes}
            />
          </div>

          <div className="report-filter-actions">
            {activeFilterCount > 0 && (
              <button
                className="oee-clear-filters-btn"
                onClick={clearAllFilters}
              >
                ✕ Clear Filters
              </button>
            )}
            <button type="button" className="btn-primary" onClick={handleGenerate} disabled={loading}>
              <HiOutlineCalendar /> Generate Report
            </button>
          </div>
          {error && <p className="report-filter-error">{error}</p>}
        </div>
      )}

      {reportData ? (
        <div style={{ display: "flex", flexDirection: "column" }}>

          {/* View Type Controls */}
          <div className="viewTypeheader">
            <div style={{ display: "flex", gap: "24px", alignItems: "center", flexWrap: "wrap", justifyContent: "space-between" }}>
              <div style={{ display: "flex", gap: "24px", alignItems: "center", flexWrap: "wrap" }}>
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <label style={{ fontSize: "14px", fontWeight: 600, color: "#1f2937" }}>
                    View Type:
                  </label>
                  <button
                    onClick={() => setViewType("table")}
                    className={viewType === "table" ? "btn btn-primary" : "btn btn-secondary"}
                  // style={{
                  //   padding: "6px 16px",
                  //   borderRadius: "6px",
                  //   border: "1px solid #e5e7eb; border-color: var(--border-primary)",
                  //   backgroundColor: viewType === "table" ? "#667eea" : "#fff",
                  //   color: viewType === "table" ? "#fff" : "#1f2937",
                  //   fontSize: "13px",
                  //   fontWeight: 500,
                  //   cursor: "pointer",
                  //   transition: "all 0.2s",
                  // }}
                  >
                    Table View
                  </button>
                  <button
                    onClick={() => setViewType("chart")}
                    className={viewType === "chart" ? "btn btn-primary" : "btn btn-secondary"}
                  // style={{
                  //   padding: "6px 16px",
                  //   borderRadius: "6px",
                  //   border: "1px solid #e5e7eb; border-color: var(--border-primary)",
                  //   backgroundColor: viewType === "chart" ? "#667eea" : "#fff",
                  //   color: viewType === "chart" ? "#fff" : "#1f2937",
                  //   fontSize: "13px",
                  //   fontWeight: 500,
                  //   cursor: "pointer",
                  //   transition: "all 0.2s",
                  // }}
                  >
                    Chart View
                  </button>
                </div>

                <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                  <label style={{ fontSize: "14px", fontWeight: 600, color: "#1f2937" }}>
                    Metrics:
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", fontSize: "13px" }}>
                    <input
                      type="checkbox"
                      checked={selectedMetrics.occurrence}
                      onChange={(e) => setSelectedMetrics({ ...selectedMetrics, occurrence: e.target.checked })}
                      style={{ cursor: "pointer" }}
                    />
                    Occurrence
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", fontSize: "13px" }}>
                    <input
                      type="checkbox"
                      checked={selectedMetrics.timeLoss}
                      onChange={(e) => setSelectedMetrics({ ...selectedMetrics, timeLoss: e.target.checked })}
                      style={{ cursor: "pointer" }}
                    />
                    Time Loss
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", fontSize: "13px" }}>
                    <input
                      type="checkbox"
                      checked={selectedMetrics.lossType}
                      onChange={(e) => setSelectedMetrics({ ...selectedMetrics, lossType: e.target.checked, lossSubType: false })}
                      style={{ cursor: "pointer" }}
                    />
                    Loss Type
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", fontSize: "13px" }}>
                    <input
                      type="checkbox"
                      checked={selectedMetrics.lossSubType}
                      onChange={(e) => setSelectedMetrics({ ...selectedMetrics, lossSubType: e.target.checked, lossType: false })}
                      style={{ cursor: "pointer" }}
                    />
                    Loss Sub Type
                  </label>
                </div>
              </div>

              {/* Timeframe Dropdown */}
              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <label style={{ fontSize: "14px", fontWeight: 600, color: "#1f2937" }}>
                  Timeframe:
                </label>
                <select
                  value={granularity}
                  onChange={handleGranularityChange}
                  style={{
                    padding: "6px 12px",
                    borderRadius: "6px",
                    border: "1px solid #e5e7eb; border-color: var(--border-primary)",
                    fontSize: "13px",
                    color: "#1f2937",
                    backgroundColor: "#fff",
                    cursor: "pointer",
                    outline: "none"
                  }}
                >
                  <option value="daily">Daily</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
            </div>
          </div>

          <div style={{ padding: "0px", marginTop: "15px", display: "flex", flexDirection: "column", gap: "32px", backgroundColor: "#f9fafb" }}>
            {reportData.series.map((seriesGroup, idx) => (
              <div key={idx} style={{ backgroundColor: "#fff", borderRadius: "8px", border: "1px solid #e5e7eb; border-color: var(--border-primary)", overflow: "hidden" }}>
                <div style={{ padding: "16px", borderBottom: "1px solid #e5e7eb", backgroundColor: "#f8fafc" }}>
                  <h3 style={{ margin: 0, fontSize: "16px", color: "#1e293b", fontWeight: 600 }}>
                    {seriesGroup.lossTypeName} Trends {selectedMetrics.lossSubType ? "(SubTypes Aggregated)" : ""}
                  </h3>
                </div>

                <div style={{ padding: "20px", display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  {/* Data Tables */}
                  {viewType === "table" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                      {seriesGroup.subTypes.map((subTypeData, stIdx) => (
                        <div key={stIdx} style={{ overflowX: "auto" }}>
                          {seriesGroup.subTypes.length > 1 && (
                            <h4 style={{ margin: "0 0 12px 0", fontSize: "14px", color: "#475569" }}>{subTypeData.name}</h4>
                          )}
                          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                            <thead>
                              <tr>
                                <th style={{ textAlign: "left", padding: "8px 12px", border: "1px solid #e2e8f0; border-color: var(--border-primary)", backgroundColor: "#f1f5f9", fontWeight: 600, color: "#475569" }}>
                                  {seriesGroup.subTypes.length > 1 ? `Metric (${subTypeData.name})` : 'Metric'}
                                </th>
                                {subTypeData.data.map((d, i) => (
                                  <th key={i} style={{ textAlign: "center", padding: "8px 12px", border: "1px solid #e2e8f0; border-color: var(--border-primary)", backgroundColor: "#f1f5f9", fontWeight: 600, color: "#475569" }}>{d.period}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {selectedMetrics.occurrence && (
                                <tr>
                                  <td style={{ padding: "8px 12px", border: "1px solid #e2e8f0; border-color: var(--border-primary)", fontWeight: 500, color: "#0f172a" }}>Loss Occurrences</td>
                                  {subTypeData.data.map((d, i) => (
                                    <td key={i} style={{ textAlign: "center", padding: "8px 12px", border: "1px solid #e2e8f0; border-color: var(--border-primary)" }}>{d.loss}</td>
                                  ))}
                                </tr>
                              )}
                              {selectedMetrics.timeLoss && (
                                <tr>
                                  <td style={{ padding: "8px 12px", border: "1px solid #e2e8f0; border-color: var(--border-primary)", fontWeight: 500, color: "#0f172a" }}>Time Loss (minutes)</td>
                                  {subTypeData.data.map((d, i) => (
                                    <td key={i} style={{ textAlign: "center", padding: "8px 12px", border: "1px solid #e2e8f0; border-color: var(--border-primary)" }}>{d.freq}</td>
                                  ))}
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Main Combo Chart */}
                  {viewType === "chart" && (
                    <div
                      style={{ height: "350px", border: "1px solid transparent", borderRadius: "8px", padding: "10px", transition: "all 0.2s" }}
                      onMouseEnter={(e) => e.currentTarget.style.borderColor = "#cbd5e1"}
                      onMouseLeave={(e) => e.currentTarget.style.borderColor = "transparent"}
                      title="Click to view detailed separation charts"
                    >
                      <h4 style={{ textAlign: "center", fontSize: "14px", color: "#64748b", marginBottom: "16px", marginTop: 0 }}>Trends Overview ({seriesGroup.lossTypeName})</h4>
                      <ResponsiveContainer width="100%" height="100%" style={{ cursor: "pointer" }}>
                        <ComposedChart
                          data={seriesGroup.data}
                          margin={{ top: 10, right: 30, left: 0, bottom: 20 }}
                          onClick={(state) => {
                            if (state && state.activePayload && state.activePayload.length > 0) {
                              handleBarClick(state.activePayload[0].payload, 0, seriesGroup);
                            } else {
                              handleChartClick(seriesGroup);
                            }
                          }}
                        >
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                          <XAxis dataKey="period" tick={{ fontSize: 12 }} tickMargin={10} axisLine={false} tickLine={false} />
                          {selectedMetrics.occurrence && (
                            <YAxis yAxisId="left" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                          )}
                          {selectedMetrics.timeLoss && (
                            <YAxis yAxisId="right" orientation={selectedMetrics.occurrence ? "right" : "left"} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                          )}
                          <Tooltip
                            contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)" }}
                          />
                          <Legend wrapperStyle={{ paddingTop: "20px" }} />
                          {selectedMetrics.occurrence && (
                            <Bar
                              yAxisId="left"
                              dataKey="loss"
                              name="Occurrences"
                              fill="#1e40af"
                              radius={[4, 4, 0, 0]}
                              maxBarSize={50}
                              onClick={(entry, index, e) => handleBarClick(entry, index, seriesGroup, e)}
                              style={{ cursor: "pointer" }}
                            />
                          )}
                          {selectedMetrics.timeLoss && (
                            <Line
                              yAxisId={selectedMetrics.occurrence ? "right" : "left"}
                              type="linear"
                              dataKey="freq"
                              name="Time Loss (min)"
                              stroke="#ea580c"
                              strokeWidth={3}
                              dot={{ r: 4, fill: "#ea580c", strokeWidth: 2, stroke: "#fff" }}
                              activeDot={{ r: 6, fill: "#ea580c", stroke: "#fff", strokeWidth: 2, onClick: (e, payload) => handleBarClick(payload.payload, 0, seriesGroup, e), style: { cursor: "pointer" } }}
                            />
                          )}
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              </div>
            ))}

          </div>
        </div>
      ) : (
        <div className="report-container">
          <div className="report-content">
            <div className="report-placeholder">
              <HiOutlinePresentationChartLine className="placeholder-icon" />
              <h3>Loss Trends Report</h3>
              <p>Select a date range and Loss Types, then click "Generate Report" to view trends</p>
            </div>
          </div>
        </div>
      )}

      {/* Modal for Detailed Charts */}
      {selectedChartData && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(15, 23, 42, 0.7)", display: "flex",
          alignItems: "center", justifyContent: "center", zIndex: 1000,
          padding: "20px"
        }}>
          <div style={{
            backgroundColor: "#fff", borderRadius: "12px", width: "100%",
            maxWidth: "1000px", maxHeight: "90vh", overflowY: "auto",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)"
          }}>
            <div style={{ padding: "20px 24px", borderBottom: "1px solid #e2e8f0; border-color: var(--border-primary)", display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", top: 0, backgroundColor: "#fff", zIndex: 10 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: "18px", color: "#0f172a" }}>
                  {selectedChartData.isSubLossBreakdown
                    ? `${selectedChartData.lossTypeName} - Subtypes Breakdown (${selectedChartData.period})`
                    : `Detailed Charts: ${selectedChartData.lossTypeName}`}
                </h2>
                <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: "#64748b" }}>
                  {selectedChartData.isSubLossBreakdown
                    ? "Breakdown of occurrences and time loss by SubType"
                    : "Separated views for Frequency and Occurrences"}
                </p>
              </div>
              <button
                onClick={closeChartModal}
                style={{ background: "none", border: "none", fontSize: "24px", cursor: "pointer", color: "#64748b", padding: "4px" }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: "32px 24px", display: "flex", flexWrap: "wrap", gap: "32px", justifyContent: "center" }}>
              {selectedChartData.isSubLossBreakdown ? (
                <div style={{ width: "100%", maxWidth: "800px", height: "400px" }}>
                  <h4 style={{ textAlign: "center", fontSize: "14px", color: "#64748b", marginBottom: "16px", marginTop: 0 }}>Subtypes Distribution</h4>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={selectedChartData.data} margin={{ top: 10, right: 30, left: 0, bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0; border-color: var(--border-primary)" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} tickMargin={10} axisLine={false} tickLine={false} />
                      {selectedMetrics.occurrence && (
                        <YAxis yAxisId="left" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                      )}
                      {selectedMetrics.timeLoss && (
                        <YAxis yAxisId="right" orientation={selectedMetrics.occurrence ? "right" : "left"} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                      )}
                      <Tooltip contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)" }} />
                      <Legend wrapperStyle={{ paddingTop: "20px" }} />
                      {selectedMetrics.occurrence && (
                        <Bar yAxisId="left" dataKey="loss" name="Occurrences" fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={50} />
                      )}
                      {selectedMetrics.timeLoss && (
                        <Bar yAxisId="right" dataKey="freq" name="Time Loss (min)" fill="#f97316" radius={[4, 4, 0, 0]} maxBarSize={50} />
                      )}
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <>
                  {/* Time Loss Chart */}
                  {selectedMetrics.timeLoss && (
                    <div style={{ width: "100%", maxWidth: "450px", height: "300px" }}>
                      <h4 style={{ textAlign: "center", fontSize: "14px", color: "#64748b", marginBottom: "16px", marginTop: 0 }}>Time Loss (minutes)</h4>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={selectedChartData.data} margin={{ top: 10, right: 10, left: -20, bottom: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0; border-color: var(--border-primary)" />
                          <XAxis dataKey="period" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                          <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                          <Tooltip contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)" }} />
                          <Bar dataKey="freq" name="Time Loss" fill="#0f766e" radius={[4, 4, 0, 0]} maxBarSize={40} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Occurrence Chart */}
                  {selectedMetrics.occurrence && (
                    <div style={{ width: "100%", maxWidth: "450px", height: "300px" }}>
                      <h4 style={{ textAlign: "center", fontSize: "14px", color: "#64748b", marginBottom: "16px", marginTop: 0 }}>Loss Occurrences</h4>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={selectedChartData.data} margin={{ top: 10, right: 10, left: -20, bottom: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0; border-color: var(--border-primary)" />
                          <XAxis dataKey="period" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                          <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                          <Tooltip contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)" }} />
                          <Bar dataKey="loss" name="Occurrences" fill="#1e40af" radius={[4, 4, 0, 0]} maxBarSize={40} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default OeeLossTrendsReport;
